import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import founderImg from '../founder.jpeg';

// ── Data ──────────────────────────────────────────────────────────────────────

const milestones = [
  {
    year: 'Oct 2022',
    title: 'Redcubical Systems Founded',
    desc: 'Incorporated in Dehradun, Uttarakhand with a single mission — build software that solves real problems for real people in India.',
    color: '#3B82F6',
  },
  {
    year: '2023',
    title: 'HealHub Launched',
    desc: 'First product shipped. HealHub clinic management software goes live — digitising healthcare one clinic at a time across Uttarakhand.',
    color: '#22C55E',
  },
  {
    year: '2024',
    title: 'HealHub Nexus Goes Live',
    desc: 'India\'s first verified professional network for doctors. Connecting Allopathic, Ayurvedic, and Homeopathic practitioners in a structured ecosystem.',
    color: '#8B5CF6',
  },
  {
    year: '2024',
    title: '500+ Clinics Milestone',
    desc: 'HealHub crosses 500 active clinics. The product proves its value in the market — growing entirely through word of mouth and referrals.',
    color: '#F59E0B',
  },
  {
    year: '2025',
    title: 'Suraksha Development Begins',
    desc: 'In response to India\'s escalating cyber fraud crisis, development begins on Suraksha — the country\'s most advanced AI anti-fraud mobile application.',
    color: '#E63946',
  },
  {
    year: '2025',
    title: 'Vedant AI Receptionist',
    desc: 'Development begins on Vedant — the world\'s first life-size AI receptionist for clinics. Powered by OpenAI. Built exclusively for HealHub subscribers.',
    color: '#06B6D4',
  },
  {
    year: '2026+',
    title: 'The Next Chapter',
    desc: 'Suraksha public launch. Vedant pilot deployment. New products in the pipeline. The mission continues — building software that protects and empowers India.',
    color: '#A855F7',
  },
];

const values = [
  {
    icon: '🎯',
    title: 'Outcome Over Output',
    desc: 'We measure success by the change we create in the world — clinics running efficiently, families protected from fraud — not by lines of code written or features shipped.',
  },
  {
    icon: '🔒',
    title: 'Trust is Non-Negotiable',
    desc: 'We handle sensitive healthcare data and personal security information. Security, privacy, and transparency are not features — they are the foundation of everything we build.',
  },
  {
    icon: '🇮🇳',
    title: 'India-First Design',
    desc: 'Every product is designed around India\'s specific realities — limited bandwidth, linguistic diversity, unique fraud patterns, and the specific needs of Indian healthcare.',
  },
  {
    icon: '🔭',
    title: 'Long-Term Thinking',
    desc: 'We build for a 10-year horizon. No shortcuts, no technical debt, no growth-at-all-costs mentality. We answer only to our users — not to quarterly targets.',
  },
  {
    icon: '❤️',
    title: 'Social Responsibility',
    desc: 'Every product we build creates measurable social good. Protecting elderly citizens from fraud. Digitising healthcare for rural India. Purpose is part of our product strategy.',
  },
  {
    icon: '🚀',
    title: 'Bootstrapped Independence',
    desc: 'No VC pressure. No compromised decisions. We are self-funded because we believe the best software is built by people who answer only to the people they serve.',
  },
];

const products = [
  {
    emoji: '🏥',
    name: 'HealHub',
    status: 'LIVE',
    statusColor: '#22C55E',
    desc: 'Complete clinic management for modern healthcare providers.',
    href: '/healhub',
  },
  {
    emoji: '🌐',
    name: 'HealHub Nexus',
    status: 'LIVE',
    statusColor: '#22C55E',
    desc: 'Professional network built exclusively for verified doctors.',
    href: '/healhub',
  },
  {
    emoji: '🛡️',
    name: 'Suraksha',
    status: 'COMING SOON',
    statusColor: '#F59E0B',
    desc: 'AI-powered anti-fraud protection for every Indian family.',
    href: '/suraksha',
  },
  {
    emoji: '🤖',
    name: 'Vedant',
    status: 'IN DEVELOPMENT',
    statusColor: '#A855F7',
    desc: 'The world\'s first life-size AI receptionist for clinics.',
    href: '/vedant',
  },
];

const stats = [
  { value: 'Oct 2022', label: 'Founded' },
  { value: '4', label: 'Products Built' },
  { value: '500+', label: 'Clinics Served' },
  { value: '₹0', label: 'VC Funded' },
];

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function About() {
  const [activeValue, setActiveValue] = useState(0);

  return (
    <div className="page-wrapper" style={{ paddingTop: '80px' }}>
      <SEOHead
        title="About Us — Redcubical Systems"
        description="Redcubical Systems is a Dehradun-based software company building AI-powered products for India — HealHub, Suraksha, Vedant. Founded in October 2022 by Ashish Uniyal. Bootstrapped, mission-driven, India-first."
        canonical="/about"
        keywords="Redcubical Systems about, Ashish Uniyal, software company Dehradun, HealHub founders, Suraksha app founders, India software startup"
      />

      {/* ── HERO ── */}
      <section
        style={{
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          padding: '80px 32px',
          position: 'relative',
          overflow: 'hidden',
          background: `
            radial-gradient(ellipse 80% 60% at 50% -10%,
              rgba(37,99,235,0.4) 0%,
              rgba(29,58,138,0.2) 40%,
              transparent 70%),
            var(--black)
          `,
        }}
      >
        <div style={{
          position: 'absolute', top: '-200px', right: '-200px',
          width: '700px', height: '700px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.18) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-100px', left: '-100px',
          width: '400px', height: '400px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{
          maxWidth: '1280px', margin: '0 auto',
          width: '100%', position: 'relative',
        }}>
          <div className="about-hero-grid">

            {/* Left */}
            <div>
              <span className="badge" style={{ marginBottom: '32px' }}>
                🇮🇳 &nbsp; Dehradun, Uttarakhand · Est. Oct 2022
              </span>

              <h1 className="display-heading" style={{ marginBottom: '24px' }}>
                WE BUILD<br />
                <span className="gradient-text">WITH</span><br />
                PURPOSE.
              </h1>

              <p style={{
                fontSize: 'clamp(16px, 2vw, 20px)',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: '1.8',
                maxWidth: '560px',
                marginBottom: '40px',
              }}>
                Redcubical Systems is a software company built on the belief that 
                technology should create measurable social good — not just generate revenue. 
                We build products that protect people, digitise critical infrastructure, 
                and empower professionals across India.
              </p>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Link to="/products" className="btn-primary">
                  See Our Products ↗
                </Link>
                <Link to="/contact" className="btn-outline">
                  Work With Us →
                </Link>
              </div>
            </div>

            {/* Right — Company card */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: '100%', maxWidth: '460px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(59,130,246,0.2)',
                borderRadius: '24px',
                padding: '40px',
              }}>
                <p style={{
                  fontSize: '11px', fontWeight: '700',
                  color: 'rgba(255,255,255,0.3)',
                  letterSpacing: '2px', textTransform: 'uppercase',
                  marginBottom: '24px',
                }}>
                  Company Profile
                </p>

                {[
                  { label: 'Legal Name', value: 'Redcubical Systems Pvt. Ltd.' },
                  { label: 'Founded', value: 'October 2022' },
                  { label: 'Headquarters', value: 'Dehradun, Uttarakhand, India' },
                  { label: 'Focus', value: 'Healthcare & Cybersecurity Software' },
                  { label: 'Funding', value: 'Bootstrapped — Self Funded' },
                  { label: 'Products', value: '4 (2 Live · 2 In Development)' },
                ].map((item) => (
                  <div key={item.label} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    padding: '14px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    gap: '16px',
                  }}>
                    <span style={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.35)',
                      flexShrink: 0,
                    }}>
                      {item.label}
                    </span>
                    <span style={{
                      fontSize: '13px',
                      color: 'rgba(255,255,255,0.75)',
                      fontWeight: '600',
                      textAlign: 'right',
                    }}>
                      {item.value}
                    </span>
                  </div>
                ))}

                <div style={{
                  marginTop: '24px',
                  padding: '16px',
                  background: 'rgba(59,130,246,0.08)',
                  border: '1px solid rgba(59,130,246,0.2)',
                  borderRadius: '12px',
                  display: 'flex', alignItems: 'center',
                  gap: '12px',
                }}>
                  <span style={{ fontSize: '20px' }}>🏥</span>
                  <div>
                    <p style={{
                      fontSize: '13px', fontWeight: '700',
                      color: '#fff', marginBottom: '2px',
                    }}>
                      HealHub.in
                    </p>
                    <a
                      href="https://healhub.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '12px',
                        color: '#3B82F6',
                        textDecoration: 'none',
                      }}
                    >
                      Visit our flagship product ↗
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section style={{
        background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(59,130,246,0.12)',
        borderBottom: '1px solid rgba(59,130,246,0.12)',
        padding: '0 32px',
      }}>
        <div style={{
          maxWidth: '1280px', margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        }} className="about-stats-grid">
          {stats.map((stat, i) => (
            <div key={i} style={{
              padding: '40px 24px', textAlign: 'center',
              borderRight: i < 3
                ? '1px solid rgba(59,130,246,0.12)' : 'none',
            }}>
              <p className="number-display" style={{ marginBottom: '8px' }}>
                {stat.value}
              </p>
              <p style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.4)',
                letterSpacing: '0.5px',
              }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── MISSION & VISION ── */}
      <section style={{ padding: '120px 32px', background: 'var(--dark)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="mission-grid">

            {/* Mission */}
            <div style={{
              padding: '56px 48px',
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(59,130,246,0.15)',
              borderRadius: '24px',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', top: '-60px', right: '-60px',
                width: '200px', height: '200px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />
              <div style={{ position: 'relative' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center', gap: '8px',
                  background: 'rgba(59,130,246,0.1)',
                  border: '1px solid rgba(59,130,246,0.25)',
                  borderRadius: '50px', padding: '6px 16px',
                  marginBottom: '28px',
                }}>
                  <span style={{
                    width: '6px', height: '6px',
                    borderRadius: '50%', background: '#3B82F6',
                  }} />
                  <span style={{
                    fontSize: '11px', fontWeight: '700',
                    color: '#3B82F6', letterSpacing: '2px',
                    textTransform: 'uppercase',
                  }}>
                    Our Mission
                  </span>
                </div>

                <h2 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 'clamp(28px, 4vw, 44px)',
                  fontWeight: '900', color: '#fff',
                  textTransform: 'uppercase',
                  lineHeight: 1.05, marginBottom: '24px',
                }}>
                  BUILT TO MAKE<br />
                  <span className="gradient-text">AN IMPACT</span>
                </h2>

                <p style={{
                  fontSize: '16px',
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: '1.85',
                  fontStyle: 'italic',
                  borderLeft: '3px solid rgba(59,130,246,0.4)',
                  paddingLeft: '20px',
                }}>
                  "To design intelligent, outcome-driven software systems that simplify 
                  complexity, accelerate decisions, and empower businesses to operate 
                  with clarity, confidence, and measurable impact."
                </p>
              </div>
            </div>

            {/* Vision */}
            <div style={{
              padding: '56px 48px',
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(139,92,246,0.15)',
              borderRadius: '24px',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', top: '-60px', right: '-60px',
                width: '200px', height: '200px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />
              <div style={{ position: 'relative' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center', gap: '8px',
                  background: 'rgba(139,92,246,0.1)',
                  border: '1px solid rgba(139,92,246,0.25)',
                  borderRadius: '50px', padding: '6px 16px',
                  marginBottom: '28px',
                }}>
                  <span style={{
                    width: '6px', height: '6px',
                    borderRadius: '50%', background: '#8B5CF6',
                  }} />
                  <span style={{
                    fontSize: '11px', fontWeight: '700',
                    color: '#8B5CF6', letterSpacing: '2px',
                    textTransform: 'uppercase',
                  }}>
                    Our Vision
                  </span>
                </div>

                <h2 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 'clamp(28px, 4vw, 44px)',
                  fontWeight: '900', color: '#fff',
                  textTransform: 'uppercase',
                  lineHeight: 1.05, marginBottom: '24px',
                }}>
                  GLOBAL LEADER IN<br />
                  <span style={{
                    background: 'linear-gradient(135deg, #A855F7 0%, #8B5CF6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}>
                    AI-DRIVEN ECOSYSTEMS
                  </span>
                </h2>

                <p style={{
                  fontSize: '16px',
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: '1.85',
                  fontStyle: 'italic',
                  borderLeft: '3px solid rgba(139,92,246,0.4)',
                  paddingLeft: '20px',
                }}>
                  "To become a global leader in AI-driven business ecosystems, where 
                  every organisation — regardless of size — can access powerful decision 
                  intelligence and operate with the precision of the world's most 
                  advanced enterprises."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOUNDER ── */}
      <section style={{
        padding: '0',
        background: 'var(--black)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
        overflow: 'hidden',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="founder-section-grid" style={{ minHeight: '600px' }}>

            {/* Left — Photo */}
            <div style={{
              position: 'relative', overflow: 'hidden',
              background: 'linear-gradient(135deg, #0A0E1A 0%, #0D1B3E 100%)',
              display: 'flex', alignItems: 'flex-end',
              justifyContent: 'center', minHeight: '540px',
            }}>
              <div style={{
                position: 'absolute', bottom: 0, left: '50%',
                transform: 'translateX(-50%)',
                width: '400px', height: '400px',
                background: 'radial-gradient(circle at bottom, rgba(37,99,235,0.3) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />
              <img
                src={founderImg}
                alt="Ashish Uniyal — Founder, Redcubical Systems"
                style={{
                  width: '100%', maxWidth: '420px',
                  height: '100%', objectFit: 'cover',
                  objectPosition: 'top center',
                  position: 'relative', zIndex: 1,
                  filter: 'contrast(1.05) brightness(0.95)',
                }}
              />
              {/* Name overlay */}
              <div style={{
                position: 'absolute', bottom: '32px',
                left: '32px', zIndex: 2,
              }}>
                <p style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '24px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  letterSpacing: '1px', marginBottom: '4px',
                }}>
                  Ashish Uniyal
                </p>
                <p style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '14px', fontWeight: '700',
                  color: 'rgba(255,255,255,0.55)',
                  textTransform: 'uppercase', letterSpacing: '2px',
                  marginBottom: '4px',
                }}>
                  Founder & CEO
                </p>
                <p style={{
                  fontSize: '12px',
                  color: 'rgba(255,255,255,0.3)',
                }}>
                  Redcubical Systems Private Limited
                </p>
              </div>
            </div>

            {/* Right — Founder story */}
            <div style={{
              padding: '80px 64px',
              background: 'rgba(5,8,16,1)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}>
              <span className="section-label" style={{ marginBottom: '16px' }}>
                The Founder
              </span>
              <h2 className="section-title" style={{ marginBottom: '28px' }}>
                BUILT FROM<br />
                <span className="gradient-text">CONVICTION</span>
              </h2>

              <p style={{
                fontSize: '16px',
                color: 'rgba(255,255,255,0.6)',
                lineHeight: '1.85',
                marginBottom: '20px',
              }}>
                Redcubical Systems was founded in October 2022 in Dehradun, Uttarakhand — 
                not in a Bangalore co-working space, not with a VC term sheet, but out of 
                a genuine conviction that India's most important sectors were 
                being underserved by software.
              </p>

              <p style={{
                fontSize: '16px',
                color: 'rgba(255,255,255,0.6)',
                lineHeight: '1.85',
                marginBottom: '20px',
              }}>
                Healthcare was still running on paper. Elderly citizens were losing their 
                savings to cyber fraudsters. The AI revolution was happening in English, 
                ignoring 1.4 billion people who spoke 22 other languages.
              </p>

              <p style={{
                fontSize: '16px',
                color: 'rgba(255,255,255,0.6)',
                lineHeight: '1.85',
                marginBottom: '40px',
              }}>
                Every product we build — HealHub, Suraksha, Vedant — is a direct answer 
                to a problem that Ashish saw happening around him and refused to accept 
                as inevitable. That is the only product strategy we have ever needed.
              </p>

              {/* Founder quote */}
              <div style={{
                padding: '24px 28px',
                background: 'rgba(59,130,246,0.06)',
                border: '1px solid rgba(59,130,246,0.2)',
                borderRadius: '16px',
                marginBottom: '32px',
              }}>
                <p style={{
                  fontSize: '15px',
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: '1.8',
                  fontStyle: 'italic',
                  marginBottom: '16px',
                }}>
                  "The most powerful product strategy is simple — find a problem that 
                  affects real people, that keeps you awake at night, and that you 
                  refuse to accept as unsolvable. Then build until it is solved."
                </p>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                }}>
                  <div style={{
                    width: '32px', height: '32px',
                    borderRadius: '50%',
                    background: 'rgba(59,130,246,0.2)',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '14px',
                  }}>
                    AU
                  </div>
                  <div>
                    <p style={{
                      fontSize: '13px', fontWeight: '700', color: '#fff',
                    }}>
                      Ashish Uniyal
                    </p>
                    <p style={{
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.35)',
                    }}>
                      Founder, Redcubical Systems
                    </p>
                  </div>
                </div>
              </div>

              <Link to="/contact" className="btn-blue">
                Talk to Ashish Directly →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR VALUES ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">What We Stand For</span>
            <h2 className="section-title" style={{ marginBottom: '20px' }}>
              THE VALUES THAT<br />
              <span className="gradient-text">DRIVE EVERY DECISION</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              These are not corporate values written for an investor deck.
              They are the beliefs that determine what we build, how we build,
              and who we build for.
            </p>
          </div>

          <div className="values-selector-grid">
            {/* Selector */}
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '8px',
            }}>
              {values.map((value, i) => (
                <button
                  key={i}
                  onClick={() => setActiveValue(i)}
                  style={{
                    display: 'flex', alignItems: 'center',
                    gap: '16px', padding: '18px 24px',
                    background: activeValue === i
                      ? 'rgba(59,130,246,0.12)'
                      : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${activeValue === i
                      ? 'rgba(59,130,246,0.4)'
                      : 'rgba(255,255,255,0.06)'}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.25s',
                    fontFamily: 'Inter, sans-serif',
                    width: '100%',
                  }}
                >
                  <span style={{ fontSize: '22px', flexShrink: 0 }}>
                    {value.icon}
                  </span>
                  <p style={{
                    fontSize: '15px', fontWeight: '700',
                    color: activeValue === i
                      ? '#fff' : 'rgba(255,255,255,0.5)',
                    transition: 'color 0.25s',
                  }}>
                    {value.title}
                  </p>
                </button>
              ))}
            </div>

            {/* Detail panel */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(59,130,246,0.2)',
              borderRadius: '24px',
              padding: '56px 48px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '320px',
            }}>
              <div style={{
                position: 'absolute', top: '-60px', right: '-60px',
                width: '300px', height: '300px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />

              <div style={{ position: 'relative' }}>
                <div style={{
                  width: '72px', height: '72px',
                  background: 'rgba(59,130,246,0.1)',
                  border: '1px solid rgba(59,130,246,0.25)',
                  borderRadius: '20px',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '36px', marginBottom: '24px',
                }}>
                  {values[activeValue].icon}
                </div>

                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: 'clamp(28px, 4vw, 44px)',
                  fontWeight: '900', color: '#fff',
                  textTransform: 'uppercase',
                  lineHeight: 1.05, marginBottom: '20px',
                }}>
                  {values[activeValue].title}
                </h3>

                <p style={{
                  fontSize: '16px',
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: '1.85',
                  maxWidth: '500px',
                }}>
                  {values[activeValue].desc}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── COMPANY TIMELINE ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <span className="section-label">Our Journey</span>
            <h2 className="section-title">
              FROM IDEA TO<br />
              <span className="gradient-text">IMPACT</span>
            </h2>
          </div>

          <div style={{
            display: 'flex', flexDirection: 'column', gap: '0',
          }}>
            {milestones.map((milestone, i) => (
              <div key={i} style={{
                display: 'flex', gap: '32px',
                paddingBottom: i < milestones.length - 1 ? '40px' : '0',
                position: 'relative',
              }}>
                {/* Vertical line */}
                {i < milestones.length - 1 && (
                  <div style={{
                    position: 'absolute',
                    left: '20px', top: '44px',
                    width: '2px',
                    height: 'calc(100% - 44px)',
                    background: `linear-gradient(180deg, ${milestone.color}40 0%, rgba(255,255,255,0.04) 100%)`,
                  }} />
                )}

                {/* Dot */}
                <div style={{
                  width: '40px', height: '40px',
                  borderRadius: '50%',
                  background: `${milestone.color}18`,
                  border: `2px solid ${milestone.color}50`,
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0, position: 'relative', zIndex: 2,
                  marginTop: '4px',
                }}>
                  <div style={{
                    width: '10px', height: '10px',
                    borderRadius: '50%',
                    background: milestone.color,
                  }} />
                </div>

                {/* Content */}
                <div style={{
                  flex: 1,
                  padding: '24px 28px',
                  background: 'rgba(255,255,255,0.02)',
                  border: `1px solid ${milestone.color}20`,
                  borderRadius: '16px',
                  transition: 'all 0.3s',
                }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = `${milestone.color}08`;
                    e.currentTarget.style.borderColor = `${milestone.color}35`;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                    e.currentTarget.style.borderColor = `${milestone.color}20`;
                  }}
                >
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    gap: '16px', marginBottom: '10px',
                    flexWrap: 'wrap',
                  }}>
                    <span style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontSize: '14px', fontWeight: '900',
                      color: milestone.color,
                      letterSpacing: '2px', textTransform: 'uppercase',
                      background: `${milestone.color}15`,
                      border: `1px solid ${milestone.color}30`,
                      padding: '4px 12px', borderRadius: '50px',
                    }}>
                      {milestone.year}
                    </span>
                  </div>
                  <h3 style={{
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontSize: '22px', fontWeight: '900',
                    color: '#fff', textTransform: 'uppercase',
                    marginBottom: '8px',
                  }}>
                    {milestone.title}
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.5)',
                    lineHeight: '1.7',
                  }}>
                    {milestone.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUR PRODUCTS ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <span className="section-label">The Ecosystem</span>
            <h2 className="section-title">
              FOUR PRODUCTS.<br />
              <span className="gradient-text">ONE MISSION.</span>
            </h2>
          </div>

          <div style={{
            display: 'grid', gap: '2px',
            background: 'rgba(59,130,246,0.08)',
            border: '1px solid rgba(59,130,246,0.12)',
            borderRadius: '20px', overflow: 'hidden',
          }} className="about-products-grid">
            {products.map((product, i) => (
              <Link
                key={i}
                to={product.href}
                style={{
                  padding: '40px 36px',
                  background: 'rgba(5,8,16,0.9)',
                  textDecoration: 'none',
                  transition: 'all 0.3s',
                  borderRight: i % 2 === 0
                    ? '1px solid rgba(59,130,246,0.08)' : 'none',
                  borderBottom: i < 2
                    ? '1px solid rgba(59,130,246,0.08)' : 'none',
                  display: 'block',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(59,130,246,0.07)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(5,8,16,0.9)';
                }}
              >
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', marginBottom: '20px',
                }}>
                  <span style={{ fontSize: '40px' }}>{product.emoji}</span>
                  <span style={{
                    fontSize: '11px', fontWeight: '700',
                    color: product.statusColor,
                    background: `${product.statusColor}15`,
                    border: `1px solid ${product.statusColor}35`,
                    padding: '4px 12px', borderRadius: '50px',
                    letterSpacing: '0.5px', textTransform: 'uppercase',
                  }}>
                    {product.status}
                  </span>
                </div>
                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '28px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '10px',
                }}>
                  {product.name}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.5)',
                  lineHeight: '1.6',
                  marginBottom: '16px',
                }}>
                  {product.desc}
                </p>
                <span style={{
                  fontSize: '13px', fontWeight: '700',
                  color: '#3B82F6',
                }}>
                  Learn More →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── LOCATION ── */}
      <section style={{
        padding: '80px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="location-grid">
            <div>
              <span className="section-label">Where We Are</span>
              <h2 style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: 'clamp(28px, 4vw, 48px)',
                fontWeight: '900', color: '#fff',
                textTransform: 'uppercase',
                lineHeight: 1.05, marginBottom: '24px',
              }}>
                BUILT IN DEHRADUN.<br />
                <span className="gradient-text">DEPLOYED ACROSS INDIA.</span>
              </h2>
              <p className="body-text" style={{ marginBottom: '32px', maxWidth: '440px' }}>
                Our headquarters are in Dehradun, Uttarakhand. We believe that 
                world-class software does not require a Mumbai or Bangalore address — 
                just the right team with the right mission.
              </p>
              <div style={{
                display: 'flex', flexDirection: 'column', gap: '12px',
              }}>
                {[
                  { icon: '📍', text: 'Dev Heights, First Floor, Aman Vihar, Sahastradhara Road, Dehradun — 248001' },
                  { icon: '📧', text: 'a.uniyal@redcubicalsystems.com' },
                  { icon: '📞', text: '+91 9368487416' },
                ].map((item) => (
                  <div key={item.text} style={{
                    display: 'flex', alignItems: 'flex-start',
                    gap: '12px', fontSize: '14px',
                    color: 'rgba(255,255,255,0.6)',
                  }}>
                    <span style={{ flexShrink: 0, fontSize: '16px' }}>
                      {item.icon}
                    </span>
                    {item.text}
                  </div>
                ))}
              </div>
            </div>

            {/* Map placeholder */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(59,130,246,0.15)',
              borderRadius: '20px',
              padding: '48px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '280px',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: `
                  linear-gradient(rgba(59,130,246,0.04) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(59,130,246,0.04) 1px, transparent 1px)
                `,
                backgroundSize: '32px 32px',
              }} />
              <div style={{ position: 'relative' }}>
                <p style={{ fontSize: '48px', marginBottom: '16px' }}>📍</p>
                <p style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '24px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '8px',
                }}>
                  Dehradun, Uttarakhand
                </p>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.4)',
                  marginBottom: '24px',
                }}>
                  248001, India
                </p>
                <a
                  href="https://maps.google.com/?q=Aman+Vihar+Sahastradhara+Road+Dehradun"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline"
                  style={{ padding: '10px 24px', fontSize: '13px' }}
                >
                  View on Google Maps ↗
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--black)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <h2 className="section-title" style={{ marginBottom: '20px' }}>
            BELIEVE IN BUILDING<br />
            <span className="gradient-text">SOMETHING THAT MATTERS?</span>
          </h2>
          <p className="section-subtitle" style={{ margin: '0 auto 40px' }}>
            Whether you are a clinic looking to digitise, a business with a 
            software problem worth solving, or a developer who wants to build 
            with purpose — we want to hear from you.
          </p>
          <div style={{
            display: 'flex', gap: '16px',
            justifyContent: 'center', flexWrap: 'wrap',
          }}>
            <Link to="/contact" className="btn-primary">
              Start a Conversation ↗
            </Link>
            <Link to="/careers" className="btn-outline">
              Join Our Team →
            </Link>
          </div>
        </div>
      </section>

      {/* ── RESPONSIVE ── */}
      <style>{`
        .about-hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        .about-stats-grid {
          grid-template-columns: repeat(4, 1fr);
        }
        .mission-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }
        .founder-section-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
        .values-selector-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          align-items: start;
        }
        .about-products-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
        }
        .location-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 64px;
          align-items: center;
        }
        @media (max-width: 1024px) {
          .values-selector-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .about-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .about-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .mission-grid {
            grid-template-columns: 1fr !important;
          }
          .founder-section-grid {
            grid-template-columns: 1fr !important;
          }
          .about-products-grid {
            grid-template-columns: 1fr !important;
          }
          .location-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
        }
      `}</style>
    </div>
  );
}